import { useState, useEffect } from 'react';
import api from '../lib/api.js';
import { formatEuro } from '../lib/format.js';
import { useApi } from '../hooks/useApi.js';
import PageHeader from '../components/ui/PageHeader.jsx';
import StatusBadge from '../components/ui/StatusBadge.jsx';
import ServiceForm from './ServiceForm.jsx';

export default function ServicesPage() {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [isActive, setIsActive] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('sort_order');
  const [sortOrder, setSortOrder] = useState('asc');

  const { data: meta, loading: metaLoading } = useApi('/services/meta');
  const qs = new URLSearchParams();
  if (search) qs.set('q', search);
  if (category) qs.set('category', category);
  if (isActive) qs.set('isActive', isActive);
  qs.set('page', page);
  qs.set('pageSize', 20);
  qs.set('sortBy', sortBy);
  qs.set('sortOrder', sortOrder);

  const { data, loading, error, refresh } = useApi('/services?' + qs.toString(), {
    deps: [search, category, isActive, page, sortBy, sortOrder],
  });

  const services = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    refresh();
  }

  return (
    <>
      <PageHeader
        title="Listino Servizi"
        subtitle={data ? `${data.total} servizi nel listino` : 'Gestione listino prezzi'}
        action={
          <button className="btn btn-primary" onClick={() => { setEditingId(null); setShowForm(true); }}>
            + Nuovo servizio
          </button>
        }
      />

      {error && <div className="p-3 rounded-md text-sm mb-4" style={{ background: 'rgba(239,68,68,.1)', color: '#ef4444' }}>{error}</div>}

      {/* Barra filtri */}
      <div className="card-elevated mb-4 flex flex-wrap items-center gap-3">
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Cerca per codice, nome, categoria..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="input w-auto" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
          <option value="">Tutte le categorie</option>
          {meta?.data?.categories?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input w-auto" value={isActive} onChange={(e) => { setIsActive(e.target.value); setPage(1); }}>
          <option value="">Tutti</option>
          <option value="true">Attivi</option>
          <option value="false">Archiviati</option>
        </select>
      </div>

      {showForm && (
        <div className="card-elevated mb-4">
          <ServiceForm clientId={editingId} onSaved={closeForm} onCancel={closeForm} />
        </div>
      )}

      {/* Tabella */}
      <div className="card-elevated overflow-x-auto">
        <table className="table-root">
          <thead>
            <tr>
              <th className="table-header">Codice</th>
              <th className="table-header">Nome</th>
              <th className="table-header">Categoria</th>
              <th className="table-header">Unità</th>
              <th className="table-header text-right">Prezzo</th>
              <th className="table-header">Stato</th>
              <th className="table-header"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className="table-cell text-center py-8" colSpan={7}>Caricamento...</td></tr>
            )}
            {!loading && services.length === 0 && (
              <tr><td className="table-cell text-center py-8" colSpan={7}>Nessun servizio trovato</td></tr>
            )}
            {!loading && services.map((s) => (
              <tr key={s.id} className="table-row cursor-pointer transition-colors" onClick={() => { setEditingId(s.id); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <td className="table-cell font-mono text-brand-300">{s.code}</td>
                <td className="table-cell">{s.name}</td>
                <td className="table-cell">{s.category}</td>
                <td className="table-cell">{s.unit}</td>
                <td className="table-cell text-right font-mono">{formatEuro(s.unit_price)}</td>
                <td className="table-cell"><StatusBadge status={s.is_active ? 'active' : 'archived'} small /></td>
                <td className="table-cell text-right">
                  <button
                    className="btn btn-ghost text-xs"
                    onClick={(e) => { e.stopPropagation(); setEditingId(s.id); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  >
                    Modifica
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--color-line)' }}>
            <span className="text-xs text-surface-4">Pagina {page} di {totalPages}</span>
            <div className="flex gap-2">
              <button className="btn btn-secondary text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prec</button>
              <button className="btn btn-secondary text-xs" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Succ →</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
